// src/components/patient/assistant/ConversationSidebar.jsx
//
// AVIA conversation history.
//
//   * grouped Today / Yesterday / Previous 7 days / Previous 30 days / Month
//     Year, computed in the patient's DISPLAY timezone from server timestamps
//     (updated_at = latest activity, written by the server on every message);
//   * newest activity first; each row shows a compact time and exposes the
//     exact timestamp on hover and keyboard focus;
//   * loading / empty / error+retry / "Load more" (server pages);
//   * desktop: a column; mobile: a drawer with backdrop, Escape to close and
//     focus returned to the toggle.

import React, { useEffect, useRef } from "react";

import { todayInZone } from "../../../lib/displayTimezone";

function dayNumber(isoDate) {
  const [y, m, d] = isoDate.split("-").map(Number);
  return Date.UTC(y, m - 1, d) / 86400000;
}

export function conversationGroup(updatedAt, zone, now = new Date()) {
  const when = new Date(updatedAt);
  if (Number.isNaN(when.getTime())) return "Older";
  const today = todayInZone(zone, now);
  const day = todayInZone(zone, when);
  const diff = dayNumber(today) - dayNumber(day);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return "Previous 7 days";
  if (diff < 30) return "Previous 30 days";
  return when.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: zone });
}

// Ordered groups; items keep latest-activity order (id breaks ties).
export function groupConversations(items, zone, now = new Date()) {
  const sorted = [...items].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at) || String(b.id).localeCompare(String(a.id)));
  const groups = [];
  sorted.forEach((item) => {
    const label = conversationGroup(item.updated_at, zone, now);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  });
  return groups;
}

// Merge a fresh first page into what is loaded: newest data wins, no duplicates.
export function mergeConversations(current, incoming) {
  const byId = new Map(current.map((item) => [item.id, item]));
  incoming.forEach((item) => byId.set(item.id, item));
  return [...byId.values()].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at) || String(b.id).localeCompare(String(a.id)));
}

export function compactTime(updatedAt, zone, now = new Date()) {
  const when = new Date(updatedAt);
  if (Number.isNaN(when.getTime())) return "";
  const group = conversationGroup(updatedAt, zone, now);
  if (group === "Today") return when.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: zone });
  if (group === "Yesterday") return "Yesterday";
  const sameYear = todayInZone(zone, now).slice(0, 4) === todayInZone(zone, when).slice(0, 4);
  return when.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }), timeZone: zone });
}

export function exactTime(updatedAt, zone) {
  const when = new Date(updatedAt);
  if (Number.isNaN(when.getTime())) return "";
  return when.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short", timeZone: zone });
}

function SidebarBody({ sessions, zone, activeId, loading, error, hasMore, loadingMore, onRetry, onLoadMore, onSelect, onNew, firstRef }) {
  const groups = groupConversations(sessions, zone);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <button
        ref={firstRef}
        type="button"
        onClick={onNew}
        className="flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-500 px-3 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95"
      >
        <span aria-hidden="true" className="text-base leading-none">+</span> New conversation
      </button>

      <nav className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1" aria-label="Conversation history" data-testid="conversation-list">
        {loading && !sessions.length ? (
          <ul className="space-y-2" aria-label="Loading conversations">
            {[0, 1, 2, 3].map((n) => <li key={n} className="h-10 animate-pulse rounded-xl bg-slate-100" />)}
          </ul>
        ) : null}
        {error ? (
          <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
            <p>{error}</p>
            <button type="button" onClick={onRetry} className="mt-2 rounded-lg border border-rose-300 bg-white px-2 py-1 font-semibold">Retry</button>
          </div>
        ) : null}
        {!loading && !error && !sessions.length ? (
          <p className="px-2 py-6 text-center text-xs text-slate-500">No conversations yet. Start one to see it here.</p>
        ) : null}

        {groups.map((group) => (
          <section key={group.label} className="mb-4" aria-label={group.label}>
            <h2 className="mb-1 px-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">{group.label}</h2>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const selected = item.id === activeId;
                const exact = exactTime(item.updated_at, zone);
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(item.id)}
                      aria-current={selected ? "true" : undefined}
                      title={`${item.title} · ${exact}`}
                      className={`group flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 ${
                        selected ? "bg-violet-50 font-semibold text-violet-800 ring-1 ring-violet-200" : "text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <span className="min-w-0 flex-1 truncate">{item.title}</span>
                      <time dateTime={item.updated_at} className="shrink-0 text-[11px] font-normal text-slate-400">
                        {compactTime(item.updated_at, zone)}
                        <span className="sr-only">, last active {exact}</span>
                      </time>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}

        {hasMore ? (
          <button type="button" onClick={onLoadMore} disabled={loadingMore}
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60">
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        ) : null}
      </nav>
    </div>
  );
}

export default function ConversationSidebar({ open, onClose, toggleRef, ...props }) {
  const drawerFirstRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    drawerFirstRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
        window.setTimeout(() => toggleRef?.current?.focus(), 0);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, toggleRef]);

  return (
    <>
      <aside className="hidden w-72 shrink-0 flex-col rounded-3xl border border-slate-200 bg-white p-3 md:flex" aria-label="Conversations">
        <SidebarBody {...props} />
      </aside>
      {open ? (
        <div className="fixed inset-0 z-50 flex md:hidden" data-testid="conversation-drawer">
          <button type="button" aria-label="Close conversations" onClick={onClose} className="absolute inset-0 bg-slate-900/40" />
          <aside role="dialog" aria-modal="true" aria-label="Conversations" className="relative flex h-full w-[85%] max-w-xs flex-col bg-white p-3 shadow-2xl">
            <SidebarBody
              {...props}
              firstRef={drawerFirstRef}
              onSelect={(id) => {
                props.onSelect(id);
                onClose();
              }}
              onNew={() => {
                props.onNew();
                onClose();
              }}
            />
          </aside>
        </div>
      ) : null}
    </>
  );
}
